import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, VariantProps } from 'class-variance-authority';
import { cn } from '@/src/utils/shadcn';

// Disabled state classes
const disabledClasses = cn('pointer-events-none opacity-60');

// Button variants configuration
const buttonVariants = cva(
  [
    // General styling
    'relative z-[2] bg-transparent text-black dark:text-black overflow-hidden text-base leading-[1.1] font-bold font-secondary tracking-wide uppercase transition-all_0.3s_linear',

    // Layout settings
    'inline-flex items-center justify-center gap-3 md:min-h-[3.75rem] min-h-[3.5rem] px-6 md:px-7 py-2 md:py-3',

    // Transition effects
    'transition-colors ease-in-out',

    // Accessibility features
    'ring-offset-primary-dark focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',

    // Default color
    'bg-primary', // You can replace bg-primary with a higher contrast color, e.g., 'bg-blue-600'

    // After element (hover effect)
    'after:absolute after:h-full after:w-0 after:bottom-0 after:right-0 after:bg-black/[.15] after:-z-1 after:transition-all_.3s_ease-in-out',

    // Hover effects
    'hover:text-black dark:hover:text-black',
    'hover:after:w-full hover:after:left-0',
  ],
  {
    variants: {
      colorScheme: {
        primary: '',
        // You can add more color schemes, e.g., 'secondary', 'danger', etc.
      },
      shape: {
        default: 'rounded-5',
        circle: 'rounded-full',
      },
    },
    defaultVariants: {
      colorScheme: 'primary',
      shape: 'default',
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, asChild = false, disabled, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';

    return (
      <Comp
        className={cn(
          disabled && disabledClasses, // Disabled state styling
          buttonVariants({ className }), // Variants handling
          className // Ensure that the `className` is passed correctly
        )}
        ref={ref}
        {...props} // Spread the remaining props, including event handlers
        aria-disabled={disabled} // Add `aria-disabled` for accessibility
      />
    );
  }
);
Button.displayName = 'Button';
